import React, { useState, useEffect, useCallback } from "react";
import { saveAs } from "file-saver";
import {
  FaCalendarAlt,
  FaClock,
  FaClipboardList,
  FaPlus,
  FaUsers,
  FaHourglassHalf,
  FaShoppingCart,
  FaInfoCircle,
} from "react-icons/fa";
import BaseSelect from "react-select";
import DatePicker from "react-datepicker";
import FixRequiredSelect from "./FixRequiredSelect";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/DatePicker.css";
import { db } from "../firebase";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import { IoMail } from "react-icons/io5";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import sv from "date-fns/locale/sv";
import ConfigService from "../services/ConfigService";

const AddBooking = () => {
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [duration, setDuration] = useState("");
  const [bookingPreview, setBookingPreview] = useState("");
  const [details, setDetails] = useState("");
  const [selectedUsers, setSelectedUsers] = useState("");
  const [selectedCart, setSelectedCart] = useState("");
  const [availableCarts, setAvailableCarts] = useState([]);
  const [isThursday, setIsThursday] = useState(false);
  const [isWeekend, setIsWeekend] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [bookingDetails, setBookingDetails] = useState(null);
  const [reservedBy, setReservedBy] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [appConfig, setAppConfig] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  registerLocale("sv", sv);
  setDefaultLocale("sv");

  useEffect(() => {
    const loadConfig = async () => {
      const config = await ConfigService.loadConfig();
      setAppConfig(config);
    };
    loadConfig();
  }, []);
  
  const getCartName = (cart) => {
    if (!appConfig) return cart;
    const cartConfig = appConfig.availableCarts.find(c => c.id === cart);
    return cartConfig ? cartConfig.name : cart;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const Select = (props) => (
    <FixRequiredSelect {...props} SelectComponent={BaseSelect} />
  );

  useEffect(() => {
    if (date && startTime) {
      const formattedDate = formatDate(date);
      const [year, month, day] = formattedDate.split("-");
      const [hours, minutes] = startTime.split(":");
      const startDate = new Date(year, month - 1, day, hours, minutes);
      const endDate = new Date(startDate.getTime() + duration * 60 * 60 * 1000);

      const formatTime = (date) => date.toTimeString().slice(0, 5);

      setBookingPreview(
        `${formattedDate} ${formatTime(startDate)}-${formatTime(endDate)}`
      );
    }
  }, [date, startTime, duration]);

  useEffect(() => {
    if (!date || !appConfig) return;

    const dayOfWeek = new Date(date).getDay();
    const isThursdayNow = dayOfWeek === 4;
    const isWeekendNow = dayOfWeek === 0 || dayOfWeek === 6;
    setIsThursday(isThursdayNow);
    setIsWeekend(isWeekendNow);

    // Get info message from config
    const message = ConfigService.getInfoMessageForWeekday(dayOfWeek);
    setInfoMessage(message || "");

    // Handle special Thursday rules
    if (isThursdayNow && appConfig.thursdaySpecialRules) {
      setSelectedCart("");
      setDetails("Vasaparken");
    } else if (!isThursdayNow) {
      setSelectedCart("");
      setDetails("");
    }
  }, [date, appConfig]);

  const fetchUnavailableCarts = useCallback(
    async (selectedDate, selectedStartTime, selectedDuration) => {
      if (!selectedDate || !selectedStartTime || !selectedDuration)
        return new Set();

      const dateString = selectedDate.toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      console.log("Fetching unavailable carts for:", dateString);

      // Check regular bookings
      const bookingsQuery = query(
        collection(db, appConfig?.database?.collections?.bookings || "booking"),
        where("dateString", "==", dateString)
      );

      // Check maintenance blocks
      const maintenanceQuery = query(
        collection(db, appConfig?.database?.collections?.maintenanceBlocks || "maintenance_blocks"),
        where("dateString", "==", dateString)
      );

      const [bookingsSnapshot, maintenanceSnapshot] = await Promise.all([
        getDocs(bookingsQuery),
        getDocs(maintenanceQuery)
      ]);

      const unavailableCarts = new Set();

      const [bookingStartHours, bookingStartMinutes] = selectedStartTime
        .split(":")
        .map(Number);
      const bookingStartTime = new Date(
        0,
        0,
        0,
        bookingStartHours,
        bookingStartMinutes
      );
      const bookingEndTime = new Date(
        bookingStartTime.getTime() + selectedDuration * 60 * 60 * 1000
      );

      // Check regular bookings
      bookingsSnapshot.forEach((doc) => {
        const booking = doc.data();
        const [bookingHours, bookingMinutes] = booking.starttime
          .split(":")
          .map(Number);
        const existingBookingStart = new Date(
          0,
          0,
          0,
          bookingHours,
          bookingMinutes
        );
        const existingBookingEnd = new Date(
          existingBookingStart.getTime() + booking.duration * 60 * 60 * 1000
        );

        if (
          (bookingStartTime < existingBookingEnd &&
            bookingEndTime > existingBookingStart) ||
          (existingBookingStart < bookingEndTime &&
            existingBookingEnd > bookingStartTime)
        ) {
          unavailableCarts.add(booking.cart);
        }
      });

      // Check maintenance blocks
      maintenanceSnapshot.forEach((doc) => {
        const block = doc.data();
        const [blockStartHours, blockStartMinutes] = block.startTime
          .split(":")
          .map(Number);
        const [blockEndHours, blockEndMinutes] = block.endTime
          .split(":")
          .map(Number);
        
        const blockStart = new Date(0, 0, 0, blockStartHours, blockStartMinutes);
        const blockEnd = new Date(0, 0, 0, blockEndHours, blockEndMinutes);

        if (
          (bookingStartTime < blockEnd && bookingEndTime > blockStart) ||
          (blockStart < bookingEndTime && blockEnd > bookingStartTime)
        ) {
          unavailableCarts.add(block.cart);
        }
      });

      return unavailableCarts;
    },
    [appConfig]
  );

  useEffect(() => {
    const updateAvailableCarts = async () => {
      if (date && startTime && duration && appConfig) {
        const unavailableCarts = await fetchUnavailableCarts(
          date,
          startTime,
          duration
        );

        let availableCartsList = appConfig.availableCarts;

        // Apply Thursday special rules if enabled
        if (isThursday && appConfig.thursdaySpecialRules) {
          availableCartsList = availableCartsList.filter(cart => cart.id === "1");
        }

        setAvailableCarts(
          availableCartsList
            .filter((cart) => !unavailableCarts.has(cart.id))
            .map((cart) => ({ label: cart.name, value: cart.id }))
        );
      }
    };

    updateAvailableCarts();
  }, [date, startTime, duration, isThursday, appConfig, fetchUnavailableCarts]);

  const generateIcsFile = (
    bookingDate,
    startTime,
    duration,
    details,
    selectedCart
  ) => {
    const [year, month, day] = bookingDate.split("-");
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const startDate = new Date(year, month - 1, day, startHours, startMinutes);
    const endDate = new Date(startDate.getTime() + duration * 60 * 60 * 1000);

    const formatDateTime = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, "");
    };

    const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:Bokning för ${getCartName(selectedCart)}
DTSTART:${formatDateTime(startDate)}
DTEND:${formatDateTime(endDate)}
DESCRIPTION:${details}
LOCATION:${details}
END:VEVENT
END:VCALENDAR
    `.trim();

    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    saveAs(blob, `bokning-${selectedCart}.ics`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setButtonDisabled(true);

    if (!date || !startTime || !appConfig) {
      console.error("Date, start time, or app config is missing");
      return;
    }

    const emailConfig = appConfig.emailSettings;
    
    if (emailConfig && emailConfig.enabled) {
      const templateParams = {
        starttime: startTime,
        duration: duration,
        date: date.toLocaleString("sv-SE", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        persons: selectedUsers,
        selectedcart: getCartName(selectedCart),
        location: details,
        reserved_by: reservedBy,
      };

      try {
        await emailjs.send(
          emailConfig.serviceId,
          emailConfig.templateId,
          templateParams,
          emailConfig.publicKey
        );
        console.log("Email sent successfully");
      } catch (error) {
        console.error("Error sending email:", error);
      }
    }

    // Convert date to a string in the format "yyyy-MM-dd"
    const formattedDate = formatDate(date);

    // Convert date and startTime to a Date object
    const [year, month, day] = formattedDate.split("-");
    const [hours, minutes] = startTime.split(":");
    const bookingDate = new Date(year, month - 1, day, hours, minutes);

    // Format the date to "2 November 2024 at 08:21:32 UTC+1"
    const formattedBookingDate = bookingDate.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });

    const dateString = date.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    try {

      const collectionName = appConfig.database?.collections?.bookings || "booking";
      await addDoc(collection(db, collectionName), {
        cart: selectedCart,
        date: formattedBookingDate,
        dateString: dateString,
        duration: Number(duration),
        location: details,
        persons: selectedUsers,
        starttime: startTime,
      });

      // Store booking details before clearing the form
      setBookingDetails({
        date: formattedDate,
        startTime,
        duration,
        details,
        selectedCart,
      });


      // Clear the form
      setDate(new Date());
      setStartTime("");
      setDuration("");
      setDetails("");
      setSelectedUsers("");
      setSelectedCart("");
      setConfirmationMessage(
        `${getCartName(selectedCart)} bokad ${bookingPreview}. ${
          reservedBy ? "Bekräftelsemail skickas inom kort." : ""
        }`
      );
      setBookingPreview("");
      setReservedBy("");
    } catch (error) {
      console.error("Error adding document: ", error);
    }
    setButtonDisabled(false);
  };

  const handleAddToCalendarClick = () => {
    if (bookingDetails) {
      const { date, startTime, duration, details, selectedCart } =
        bookingDetails;
      generateIcsFile(date, startTime, duration, details, selectedCart);
    } else {
      console.error("No booking details available for calendar event");
    }
  };

  const generateTimeOptions = () => {
    if (!appConfig) return [];
    const options = [];
    const timeSettings = ConfigService.getTimeSettings();
    const startHour = isWeekend ? timeSettings.weekendStartHour : timeSettings.weekdayStartHour;
    const endHour = isWeekend ? timeSettings.weekendEndHour - 1 : timeSettings.weekdayEndHour - 1;

    for (let i = startHour; i <= endHour; i++) {
      for (let j = 0; j < 60; j += 30) {
        const time = `${i.toString().padStart(2, "0")}:${j
          .toString()
          .padStart(2, "0")}`;
        options.push({ label: time, value: time });
      }
    }
    return options;
  };

  const generateDurationOptions = () => {
    if (!appConfig || !startTime) return [];
    
    const options = [];
    const timeSettings = ConfigService.getTimeSettings();
    const [hours, minutes] = startTime.split(":").map(Number);
    const startDate = new Date(0, 0, 0, hours, minutes);
    const endLimit = new Date(0, 0, 0, isWeekend ? timeSettings.weekendEndHour : timeSettings.weekdayEndHour, 0);
    const maxDuration = Math.min(
      timeSettings.maxBookingDuration,
      (endLimit - startDate) / (1000 * 60 * 60)
    );

    for (let i = 0.5; i <= maxDuration; i += 0.5) {
      options.push({ label: `${i} timmar`, value: i });
    }
    return options;
  };

  const generateThursdayTimeSlots = () => {
    return [
      "08:00-10:00",
      "10:00-11:30",
      "11:30-12:30",
      "12:30-13:30",
      "13:30-14:30",
      "14:30-15:30",
      "15:30-16:30",
      "16:30-18:00",
    ].map((timeSlot) => ({ label: timeSlot, value: timeSlot }));
  };

  const handleTimeSlotChange = (option) => {
    const [start, end] = option.value.split("-");
    setStartTime(start);

    const [startHours, startMinutes] = start.split(":").map(Number);
    const [endHours, endMinutes] = end.split(":").map(Number);

    const startDate = new Date(0, 0, 0, startHours, startMinutes);
    const endDate = new Date(0, 0, 0, endHours, endMinutes);

    const durationInHours = (endDate - startDate) / (1000 * 60 * 60);
    setDuration(durationInHours);
  };

  const calculateEndTime = (startTime, duration) => {
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const startDate = new Date(0, 0, 0, startHours, startMinutes);
    const endDate = new Date(startDate.getTime() + duration * 60 * 60 * 1000);
    return `${endDate.getHours().toString().padStart(2, "0")}:${endDate
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  };


  // Don't render until config is loaded
  if (!appConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4 sm:p-6">
        <div className="card max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laddar konfiguration...</p>
        </div>
      </div>
    );
  }

  // Calculate the maximum date for booking
  const maxBookingDate = new Date();

  maxBookingDate.setDate(maxBookingDate.getDate() + appConfig.maxBookingDays);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 flex items-center">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
              <FaPlus className="text-primary-600 text-xl" />
            </div>
            Ny Bokning
          </h2>
          <p className="text-gray-600 ml-16">Skapa en ny bokning för vagn</p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6 animate-slide-up">
          <div>
            <label className="label flex items-center">
              <FaCalendarAlt className="mr-2 text-primary-600" />
              Välj Datum
            </label>
            <DatePicker
              selected={date}
              placeholderText="Välj datum"
              onChange={(date) => setDate(date)}
              className="input-field"
              dateFormat="yyyy/dd/MM"
              maxDate={maxBookingDate}
              minDate={new Date()}
              required
              locale="sv"
              showWeekNumbers
              weekLabel="V"
            />
          </div>

          {infoMessage && (
            <div className="bg-primary-50 border-l-4 border-primary-500 rounded-lg p-4">
              <div className="flex items-start">
                <FaInfoCircle className="text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700 leading-relaxed">
                  {infoMessage}
                </div>
              </div>
            </div>
          )}

          {isThursday && appConfig.thursdaySpecialRules ? (
            <div>
              <label className="label flex items-center">
                <FaClock className="mr-2 text-primary-600" />
                Bokningstid
              </label>
              <Select
                value={generateThursdayTimeSlots().find(
                  (option) =>
                    option.value ===
                    `${startTime}-${calculateEndTime(startTime, duration)}`
                )}
                onChange={handleTimeSlotChange}
                options={generateThursdayTimeSlots()}
                placeholder="Välj tid"
                required
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label flex items-center">
                  <FaClock className="mr-2 text-primary-600" />
                  Starttid
                </label>
                <Select
                  value={generateTimeOptions().find(
                    (option) => option.value === startTime
                  )}
                  onChange={(option) => setStartTime(option.value)}
                  options={generateTimeOptions()}
                  placeholder="Välj tid"
                  required
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="label flex items-center">
                  <FaHourglassHalf className="mr-2 text-primary-600" />
                  Varaktighet
                </label>
                <Select
                  value={generateDurationOptions().find(
                    (option) => option.value === duration
                  )}
                  onChange={(option) => setDuration(option.value)}
                  options={generateDurationOptions()}
                  placeholder="Välj varaktighet"
                  required
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          )}

          {bookingPreview && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">Bokningstid:</span> {bookingPreview}
              </p>
            </div>
          )}
          
          <div>
            <label className="label flex items-center">
              <FaUsers className="mr-2 text-primary-600" />
              Namn
            </label>
            <input
              type="text"
              value={selectedUsers}
              onChange={(e) => setSelectedUsers(e.target.value)}
              className="input-field"
              placeholder="Ange namn på personer"
              required
            />
          </div>

          <div>
            <label className="label flex items-center">
              <FaShoppingCart className="mr-2 text-primary-600" />
              Välj Vagn
            </label>
            <Select
              value={availableCarts.find(
                (option) => option.value === selectedCart
              )}
              onChange={(option) => setSelectedCart(option.value)}
              options={availableCarts}
              placeholder="Välj en vagn"
              required
              noOptionsMessage={() => "Inga vagnar tillgängliga..."}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div>
            <label className="label flex items-center">
              <FaClipboardList className="mr-2 text-primary-600" />
              Plats / Övrig bokningsinformation
            </label>
            <textarea
              placeholder="Beskriv plats och övrig information..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="input-field h-32 resize-none"
              required
            />
          </div>

          {appConfig.emailSettings?.enabled && (
            <div>
              <label className="label flex items-center">
                <IoMail className="mr-2 text-primary-600" />
                E-post för bekräftelse (valfritt)
              </label>
              <input
                type="email"
                value={reservedBy}
                onChange={(e) => setReservedBy(e.target.value)}
                className="input-field"
                placeholder="din@email.com"
              />
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                required
                className="mt-1 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="ml-3 text-sm text-gray-700 leading-relaxed">
                Jag bekräftar att alla som är med på bokningen har genomgått
                utbildning för vagntjänst.
              </span>
            </label>
          </div>

          <button
            disabled={buttonDisabled}
            type="submit"
            className="btn-primary w-full"
          >
            <FaPlus className="mr-2 flex-shrink-0" />
            <span>Skapa Bokning</span>
          </button>
        </form>

        {confirmationMessage && (
          <div className="card mt-6 bg-green-50 border-green-200 animate-fade-in">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaPlus className="text-2xl text-green-600 transform rotate-45" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Bokning skapad!</h3>
              <p className="text-gray-700 mb-4">{confirmationMessage}</p>
              <button
                onClick={handleAddToCalendarClick}
                className="btn-secondary text-sm"
              >
                Lägg till i kalender
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddBooking;
